import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link } from '@inertiajs/react'

interface Community {
  id: number
  name: string
}

export interface FormData {
  pseudo: string
  first_name: string
  last_name: string
  email: string
  rpps: string
  specialty: string
  role: string
  community_id: string
  password: string
  password_confirmation: string
  internal_account: boolean
}

interface UserFormProps {
  data: FormData
  setData: <K extends keyof FormData>(key: K, value: FormData[K]) => void
  errors: Partial<Record<keyof FormData, string>>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  communities: Community[]
  specialties: string[]
  roles: string[]
  isEdit?: boolean
}

function getRoleLabel(role: string): string {
  switch (role) {
    case 'admin': return 'Administrateur'
    case 'moderator': return 'Modérateur'
    default: return 'Membre'
  }
}

export function UserForm({
  data,
  setData,
  errors,
  processing,
  onSubmit,
  communities,
  specialties,
  roles,
  isEdit = false
}: UserFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <Card className="p-6 mb-6">
        <h3 className="text-lg font-display font-bold text-text-dark mb-4">
          Informations personnelles
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-text-dark mb-1">
              Prenom *
            </label>
            <Input
              id="first_name"
              type="text"
              value={data.first_name}
              onChange={(e) => setData('first_name', e.target.value)}
              placeholder="Jean"
              required
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-text-dark mb-1">
              Nom *
            </label>
            <Input
              id="last_name"
              type="text"
              value={data.last_name}
              onChange={(e) => setData('last_name', e.target.value)}
              placeholder="Dupont"
              required
            />
            {errors.last_name && (
              <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
            )}
          </div>

          <div>
            <label htmlFor="pseudo" className="block text-sm font-medium text-text-dark mb-1">
              Pseudo *
            </label>
            <Input
              id="pseudo"
              type="text"
              value={data.pseudo}
              onChange={(e) => setData('pseudo', e.target.value)}
              placeholder="Dr. Dupont"
              required
            />
            {errors.pseudo && (
              <p className="text-red-500 text-sm mt-1">{errors.pseudo}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-dark mb-1">
              Email *
            </label>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              placeholder="jean.dupont@example.com"
              required
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {!data.internal_account && (
            <div>
              <label htmlFor="rpps" className="block text-sm font-medium text-text-dark mb-1">
                Numero RPPS *
              </label>
              <Input
                id="rpps"
                type="text"
                value={data.rpps}
                onChange={(e) => setData('rpps', e.target.value)}
                placeholder="12345678901"
                maxLength={11}
                required
              />
              {errors.rpps && (
                <p className="text-red-500 text-sm mt-1">{errors.rpps}</p>
              )}
              <p className="text-xs text-text-gray mt-1">11 chiffres</p>
            </div>
          )}

          <div>
            <label htmlFor="specialty" className="block text-sm font-medium text-text-dark mb-1">
              Specialite
            </label>
            <Input
              id="specialty"
              type="text"
              value={data.specialty}
              onChange={(e) => setData('specialty', e.target.value)}
              placeholder="Medecine generale"
              list="specialties-list"
            />
            <datalist id="specialties-list">
              {specialties.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            {errors.specialty && (
              <p className="text-red-500 text-sm mt-1">{errors.specialty}</p>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-display font-bold text-text-dark mb-4">
          Role et communaute
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-text-dark mb-1">
              Role *
            </label>
            <Select
              id="role"
              value={data.role}
              onChange={(e) => setData('role', e.target.value)}
              required
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {getRoleLabel(role)}
                </option>
              ))}
            </Select>
            {errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role}</p>
            )}
          </div>

          <div>
            <label htmlFor="community_id" className="block text-sm font-medium text-text-dark mb-1">
              Communaute *
            </label>
            <Select
              id="community_id"
              value={data.community_id}
              onChange={(e) => setData('community_id', e.target.value)}
              required
            >
              <option value="">Selectionner une communaute</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
            {errors.community_id && (
              <p className="text-red-500 text-sm mt-1">{errors.community_id}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.internal_account}
                onChange={(e) => setData('internal_account', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-tessan-green focus:ring-tessan-green"
              />
              <span className="text-sm font-medium text-text-dark">
                Compte interne (sans RPPS/SIRET)
              </span>
            </label>
            <p className="text-xs text-text-gray mt-1 ml-6">
              Permet de creer un compte sans identifiant professionnel pour les membres de l'equipe Tessan.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-display font-bold text-text-dark mb-4">
          {isEdit ? 'Changer le mot de passe' : 'Mot de passe'}
        </h3>
        {isEdit && (
          <p className="text-sm text-text-gray mb-4">
            Laissez vide pour conserver le mot de passe actuel.
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-dark mb-1">
              Mot de passe {!isEdit && '*'}
            </label>
            <Input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData('password', e.target.value)}
              placeholder="••••••••"
              required={!isEdit}
              minLength={6}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
            <p className="text-xs text-text-gray mt-1">Minimum 6 caracteres</p>
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-text-dark mb-1">
              Confirmer le mot de passe {!isEdit && '*'}
            </label>
            <Input
              id="password_confirmation"
              type="password"
              value={data.password_confirmation}
              onChange={(e) => setData('password_confirmation', e.target.value)}
              placeholder="••••••••"
              required={!isEdit}
            />
            {errors.password_confirmation && (
              <p className="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Link href="/admin/users">
          <Button type="button" variant="secondary">
            Annuler
          </Button>
        </Link>
        <Button type="submit" disabled={processing}>
          {processing ? 'Enregistrement...' : (isEdit ? 'Mettre a jour' : 'Creer')}
        </Button>
      </div>
    </form>
  )
}
